import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

function AdminAuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, error, unauthorized
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    processAuth();
  }, []);

  const processAuth = async () => {
    try {
      // Get session_id from URL fragment
      const hash = window.location.hash;
      if (!hash || !hash.includes('session_id=')) {
        setStatus('error');
        setErrorMessage('No session ID found in URL');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      const sessionId = hash.split('session_id=')[1].split('&')[0];
      
      // Exchange session_id for user data
      const response = await axios.post(
        `${API}/api/auth/session`,
        { session_id: sessionId },
        { 
          withCredentials: true,
          timeout: 10000
        }
      );

      // Set cookie
      document.cookie = `session_token=${response.data.session_token}; path=/; max-age=${7 * 24 * 60 * 60}; samesite=none; secure`;
      
      const user = response.data.user;

      // Check if user has @emergent.sh email (admin role)
      if (user.role === 'admin') {
        // Success! Redirect to admin dashboard
        console.log('✅ Admin access granted');
        window.location.href = '/em-admin-access/tracking';
      } else {
        // Not an @emergent.sh email
        setStatus('unauthorized');
        setErrorMessage('Access denied. Only @emergent.sh emails are allowed.');
        
        // Logout and redirect to homepage after 3 seconds
        setTimeout(async () => {
          try {
            await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
            document.cookie = 'session_token=; path=/; max-age=0';
          } catch (err) {
            console.error('Logout error:', err);
          }
          window.location.href = '/';
        }, 3000);
      }
    } catch (error) {
      console.error('Auth processing failed:', error);
      setStatus('error');
      setErrorMessage('Authentication failed. Please try again.');
      setTimeout(() => navigate('/'), 3000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Verifying Access...</h2>
            <p className="text-gray-600">Please wait while we verify your credentials.</p>
          </>
        )}
        
        {status === 'unauthorized' && (
          <>
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <p className="text-sm text-gray-500">Redirecting to homepage...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Error</h2>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <p className="text-sm text-gray-500">Redirecting to homepage...</p>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminAuthCallback;
